import { Icon } from '@iconify/react';
import {
  Button, IconButton, Paper, Skeleton, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography
} from '@mui/material';
import React, { useMemo, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ControlledTextField } from 'src/components/forms';
import { ICON_MAS } from 'src/pages/sistema/components/SharedIcons';
import { LCSLHPTIGroupedHeader, LCSLHPTIRow } from '../hooks/useLCSLHPTITablaValor';
import { OperacionLCSLHPTIFields, OperacionLCSLHPTITablaValorFields } from '../types/fields';
import TablaValorCeldaLCSLHPTI from './TablaValorCeldaLCSLHPTI';

interface LCSLHPTITablaValoresEditorGridProps {
  formMethods: UseFormReturn<any>;
  groupedHeaders: LCSLHPTIGroupedHeader[];
  rows: LCSLHPTIRow[];
  isLoading: boolean;
  selectoresMapping: Record<string, any>;
  onAgregarOtros: () => void;
  onEliminarOtros: (rowKey: string | number) => void;
  permiteEditar?: boolean;
}

const LCSLHPTITablaValoresEditorGrid = ({
  formMethods,
  groupedHeaders,
  rows,
  isLoading,
  selectoresMapping,
  onAgregarOtros,
  onEliminarOtros,
  permiteEditar = true
}: LCSLHPTITablaValoresEditorGridProps): JSX.Element => {
  const { control, setValue, getValues } = formMethods;
  const timerRef = useRef<any>(null);

  const indexMap = useMemo(() => {
    const map = new Map<string, number>();
    const actuales = (getValues(OperacionLCSLHPTIFields.TablaValores) || []) as any[];
    
    actuales.forEach((v, idx) => {
      const codInst = v[OperacionLCSLHPTITablaValorFields.CodInstrumento];
      const descInst = v[OperacionLCSLHPTITablaValorFields.DescInstrumento];
      const codTipo = v[OperacionLCSLHPTITablaValorFields.CodOperacionTipo];
      
      // si tiene rowId
      const rowKey = v.rowId || (codInst === 15 ? `${codInst}-${descInst}` : codInst);
      
      map.set(`${rowKey}-${codTipo}`, idx);
    });
    
    return map;
  }, [rows, getValues]);

  const renderRow = (row: LCSLHPTIRow): JSX.Element => {
    const actuales = getValues(OperacionLCSLHPTIFields.TablaValores) || [];
    const primeraCelda = Object.values(row.celdas)[0] as any;
    // const indexNombre = actuales.findIndex((v: any) =>
    //   (v.rowId || v[OperacionLCSLHPTITablaValorFields.CodInstrumento]) === row.rowKey
    // );

    const indexNombre = primeraCelda?.originalIndex;

    return (
      <TableRow key={row.rowKey} hover>
        <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50', borderRight: 1, borderColor: 'divider', minWidth: 200 }}>
          {row.permiteEditarInstrumento && permiteEditar ? (
            <Stack direction='row' alignItems='center' spacing={1}>
              <ControlledTextField
                name={`${OperacionLCSLHPTIFields.TablaValores}.${indexNombre}.${OperacionLCSLHPTITablaValorFields.DescInstrumento}`}
                control={control}
                size="small"
                fullWidth
                
                // para settearle el isntrumento a todos los elementos de la fila
                onChangeCapture={(e: any) => {
                  const nuevoNombre = e.target.value;
                  if (timerRef.current) clearTimeout(timerRef.current);
                  timerRef.current = setTimeout(() => {
                    const dataActual = getValues(OperacionLCSLHPTIFields.TablaValores);
                    dataActual.forEach((item: any, idx: number) => {
                      const itemKey = item.rowId || item[OperacionLCSLHPTITablaValorFields.CodInstrumento];
                      if (itemKey === row.rowKey && idx !== indexNombre) {
                        setValue(`${OperacionLCSLHPTIFields.TablaValores}.${idx}.${OperacionLCSLHPTITablaValorFields.DescInstrumento}`, nuevoNombre);
                      }
                    });
                  }, 300);
                }}
              />
              <Tooltip title='Eliminar'>
                <IconButton size='small' onClick={() => onEliminarOtros(row.rowKey)}>
                  <Icon icon='tabler:trash' color='red' />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.descInstrumento}</Typography>
          )}
        </TableCell>

        {groupedHeaders.map(group => (
          <React.Fragment key={group.agrupacion}>
            {group.columnas.map(col => {
              const key = `${row.rowKey}-${col.codOperacionTipo}`;
              const indexEnArrayPlano = indexMap.get(key);
              // const celdaData = indexEnArrayPlano !== undefined ? actuales[indexEnArrayPlano] : null;

              const celdaData = row.celdas[col.codOperacionTipo] as any;

              return (
                <TableCell
                  key={key}
                  align="center"
                  sx={{ borderRight: 1, borderColor: 'divider', minWidth: 110, p: 0.5 }}
                >
                  
                  {celdaData && indexEnArrayPlano !== undefined ? (
                    <TablaValorCeldaLCSLHPTI
                      control={control}
                      index={celdaData.indexOriginal}
                      tipoSelector={celdaData[OperacionLCSLHPTITablaValorFields.TipoSelector]}
                      loadOptionsMapping={selectoresMapping}
                      permiteEditar={permiteEditar}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">- {JSON.stringify(indexEnArrayPlano)}</Typography>
                  )}
                </TableCell>
              );
            })}
          </React.Fragment>
        ))}
      </TableRow>
    );
  };

  if (isLoading) return <Skeleton variant='rectangular' height={600} />;

  const filasReales = rows.filter(r => r.codInstrumento !== 15);
  const filasOtros = rows.filter(r => r.codInstrumento === 15);

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ backgroundColor: 'grey.100', fontWeight: 'bold', borderRight: 1, borderColor: 'divider' }}>Instrumento</TableCell>
            {groupedHeaders.map((group) => (
              <TableCell key={group.agrupacion} align="center" colSpan={group.columnas.length} sx={{ backgroundColor: 'grey.100', fontWeight: 'bold', borderRight: 1, borderColor: 'divider', fontSize: '0.75rem' }}>
                {group.agrupacion}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={{ borderRight: 1, borderColor: 'divider', backgroundColor: 'grey.50' }} />
            {groupedHeaders.map(group => (
              <React.Fragment key={`sub-${group.agrupacion}`}>
                {group.columnas.map(col => (
                  <TableCell key={col.codOperacionTipo} align="center" sx={{ fontWeight: 'medium', fontSize: '0.65rem', borderRight: 1, borderColor: 'divider', width: 110 }}>
                    {col.descOperacionTipo}
                  </TableCell>
                ))}
              </React.Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filasReales.map(renderRow)}
          {permiteEditar && (
            <TableRow>
              <TableCell sx={{ p: 0, backgroundColor: 'grey.50', borderRight: 1, borderColor: 'divider' }}>
                <Button variant="contained" color="primary" endIcon={ICON_MAS} onClick={onAgregarOtros} fullWidth sx={{ justifyContent: 'flex-start', borderRadius: 0, fontSize: '0.75rem' }}>
                  Instrumento
                </Button>
              </TableCell>
              {groupedHeaders.map(group => (
                <React.Fragment key={`spacer-group-${group.agrupacion}`}>
                  {group.columnas.map(col => (
                    <TableCell key={`spacer-${col.codOperacionTipo}`} sx={{ backgroundColor: 'grey.50', borderRight: 1, borderColor: 'divider', opacity: 0.5 }} />
                  ))}
                </React.Fragment>
              ))}
            </TableRow>
          )}
          {filasOtros.map(renderRow)}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LCSLHPTITablaValoresEditorGrid;